export const TipoVenta = Object.freeze({
  Boleta: 'Boleta',
  Factura: 'Factura',
  GuiaDespacho: 'GuiaDespacho',
  Redistribucion: 'Redistribucion',
  Otro: 'Otro'
})

const TIPO_IMPRESION_POR_TIPO_VENTA = {
  BOLETA: 'factura',
  FACTURA: 'factura',
  GUIADESPACHO: 'entrega',
  REDISTRIBUCION: 'transferencia'
}

const normalizarTipoVenta = (tipoVenta) =>
  String(tipoVenta ?? '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z]/g, '')

const normalizarDocEntry = (valor) => {
  if (valor === null || valor === undefined) return null
  const numero = Number(valor)
  if (Number.isFinite(numero) && numero > 0) return numero
  return null
}

const normalizarClave = (clave) =>
  String(clave)
    .toLowerCase()
    .replace(/[^a-z]/g, '')

const resolverFADocEntry = (pedido) => {
  if (!pedido || typeof pedido !== 'object') return null

  const directo = normalizarDocEntry(
    pedido.FADocEntry ?? pedido.faDocEntry ?? pedido.fADocEntry
  )
  if (directo) return directo

  const buscarEnObjeto = (obj) => {
    if (!obj || typeof obj !== 'object') return null
    const claveDinamica = Object.keys(obj).find((clave) => {
      const claveNormalizada = normalizarClave(clave)
      return (
        claveNormalizada === 'fadocentry' ||
        claveNormalizada.endsWith('fadocentry') ||
        (claveNormalizada.includes('docentry') &&
          (claveNormalizada.includes('factura') ||
            claveNormalizada.includes('deudor') ||
            claveNormalizada.includes('fa')))
      )
    })
    if (!claveDinamica) return null
    return normalizarDocEntry(obj[claveDinamica])
  }

  const visitados = new WeakSet()
  const buscarRecursivo = (valor, profundidad = 0) => {
    if (!valor || typeof valor !== 'object') return null
    if (visitados.has(valor) || profundidad > 10) return null
    visitados.add(valor)

    const encontrado = buscarEnObjeto(valor)
    if (encontrado) return encontrado

    const valores = Array.isArray(valor) ? valor : Object.values(valor)
    for (const item of valores) {
      const enHijo = buscarRecursivo(item, profundidad + 1)
      if (enHijo) return enHijo
    }

    return null
  }

  return buscarRecursivo(pedido)
}

const resolverDocEntryDesdeDocsPorPaso = (docsPorPaso, tipo) => {
  const docs = Array.isArray(docsPorPaso?.[tipo]) ? docsPorPaso[tipo] : []
  const doc = docs.find((item) => normalizarDocEntry(item?.docEntry))
  return normalizarDocEntry(doc?.docEntry)
}

const resolverFacturaDesdeDocs = (docsPorPaso) => {
  const docsFactura = Array.isArray(docsPorPaso?.factura)
    ? docsPorPaso.factura
    : []
  if (docsFactura.length === 0) return null

  const facturaDeudor = docsFactura.find((doc) => {
    const texto = String(doc?.tipo ?? doc?.comments ?? '')
      .trim()
      .toLowerCase()
    return texto.includes('deudor') && normalizarDocEntry(doc?.docEntry)
  })
  if (facturaDeudor) {
    return normalizarDocEntry(facturaDeudor.docEntry)
  }

  return resolverDocEntryDesdeDocsPorPaso(docsPorPaso, 'factura')
}

const resolverInvoiceDocEntryDesdeItems = (pedido) => {
  const items = Array.isArray(pedido?.items) ? pedido.items : []
  const itemConDocEntry = items.find((item) =>
    normalizarDocEntry(item?.invoiceDocEntry ?? item?.InvoiceDocEntry)
  )
  if (!itemConDocEntry) return null

  return normalizarDocEntry(
    itemConDocEntry.invoiceDocEntry ?? itemConDocEntry.InvoiceDocEntry
  )
}

export const resolverDocumentoSapDesdePedido = (pedido) => {
  const tipoVentaNormalizado = normalizarTipoVenta(pedido?.tipoVenta)
  const tipo = TIPO_IMPRESION_POR_TIPO_VENTA[tipoVentaNormalizado] ?? null
  const docEntryPrincipal = resolverInvoiceDocEntryDesdeItems(pedido)
  const docEntryFactura = resolverFADocEntry(pedido)

  const docEntry =
    tipo === 'factura'
      ? docEntryPrincipal || docEntryFactura
      : docEntryPrincipal

  if (!tipo || !docEntry) return null
  return { tipo, docEntry }
}

export const resolverDocumentoSapDesdeContexto = ({
  pedido,
  pedidos,
  docsPorPaso
}) => {
  const docFactura = resolverFacturaDesdeDocs(docsPorPaso)
  if (docFactura) return { tipo: 'factura', docEntry: docFactura }

  const documentoDesdePedido = resolverDocumentoSapDesdePedido(pedido)
  if (documentoDesdePedido?.docEntry) return documentoDesdePedido

  const candidatos = Array.isArray(pedidos) ? pedidos : []
  for (const candidato of candidatos) {
    const documento = resolverDocumentoSapDesdePedido(candidato)
    if (documento?.docEntry) return documento
  }

  const docTransferencia = resolverDocEntryDesdeDocsPorPaso(
    docsPorPaso,
    'transferencia'
  )
  if (docTransferencia) {
    return { tipo: 'transferencia', docEntry: docTransferencia }
  }

  const docEntrega = resolverDocEntryDesdeDocsPorPaso(docsPorPaso, 'entrega')
  if (docEntrega) return { tipo: 'entrega', docEntry: docEntrega }

  return null
}

export const esDocumentoSapImprimible = (pedido) =>
  Boolean(resolverDocumentoSapDesdePedido(pedido))
